import { useMemo, useState } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Check, ChevronDown } from 'lucide-react';
import { IMAGE_MODEL_OPTIONS } from '../../lib/imageModels';

function ModelSelector({
  value = 'auto',
  onChange = () => {},
  disabled = false,
  label = '',
  options = IMAGE_MODEL_OPTIONS,
  variant = 'pill', // 'pill' | 'compact'
  theme = 'default', // 'default' | 'neutral'
}) {
  const [open, setOpen] = useState(false);
  const isNeutral = theme === 'neutral';

  const selected = useMemo(() => {
    // Handle empty options array
    if (!options || options.length === 0) {
      return { value: value || 'auto', label: 'Loading...' };
    }
    const found = options.find((o) => o.value === value);
    // If value is 'auto' but not in options, show "Auto (default)" label
    if (!found && value === 'auto') {
      return { value: 'auto', label: 'Auto (default)' };
    }
    // If value is not found in options, show the first option but keep the value
    // This handles cases where the model ID is valid but options haven't loaded yet
    if (!found) {
      return { value: value, label: options[0]?.label || 'Select model' };
    }
    return found;
  }, [value, options]);

  return (
    <div
      className={`model-selector ${variant === 'compact' ? 'model-selector--compact' : ''} ${
        isNeutral ? 'model-selector--neutral' : ''
      }`}
    >
      {!!label && <span className="model-selector-label">{label}</span>}

      <DropdownMenu.Root open={open} onOpenChange={setOpen} modal={false}>
        <DropdownMenu.Trigger asChild>
          <button
            type="button"
            className="model-selector-trigger"
            aria-haspopup="menu"
            aria-expanded={open}
            disabled={disabled}
          >
            {selected?.thumbnail && (
              <span className="model-selector-thumb">
                <img src={selected.thumbnail} alt="" draggable={false} />
              </span>
            )}
            <span className="model-selector-trigger-value" title={selected?.label}>
              {selected?.label || 'Auto (default)'}
            </span>
            <ChevronDown
              size={variant === 'compact' ? 14 : 16}
              className={`model-selector-icon ${open ? 'model-selector-icon-open' : ''}`}
            />
          </button>
        </DropdownMenu.Trigger>

        <DropdownMenu.Portal>
          <DropdownMenu.Content
            className={`model-selector-content ${isNeutral ? 'model-selector-content--neutral' : ''}`}
            sideOffset={8}
            align="end"
          >
            {options.map((opt) => (
              <DropdownMenu.Item
                key={opt.value}
                className={`model-selector-item ${isNeutral ? 'model-selector-item--neutral' : ''}`}
                onSelect={() => onChange(opt.value)}
                disabled={disabled}
              >
                <span className="model-selector-item-label" title={opt.label}>
                  {opt.label}
                </span>
                {value === opt.value && (
                  <Check size={16} className={`model-selector-check ${isNeutral ? 'model-selector-check--neutral' : ''}`} />
                )}
              </DropdownMenu.Item>
            ))}
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </div>
  );
}

export default ModelSelector;


